import React, { useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { Disclosure } from '@headlessui/react';
import blokPublicStore from '@/js/modules/bloks/store/BlokPublicStore';
import { PageRoutes } from '@/utils/PageRoutes';
import { __ } from '@/utils/Trans';
import { Icons } from '@/utils/Icons';
import Icon from '@/js/components/Icon/Icon';

export interface ISubcategory {
	to: string;
	translationKey: string;
}

interface ITopBarNavigationItem extends React.PropsWithChildren {
	item: {
		to: string;
		name: string;
		translation: string;
		openInNewTab?: boolean;
		reloadDocument?: boolean;
		element?: React.ReactNode | JSX.Element;
		subcategories?: ISubcategory[];
		translationKey?: string;
	};
	getClassNames: (isActive: boolean) => string;
	isMobile?: boolean;
}

const TopNavBarMenuItem = ({ item, getClassNames, isMobile = false }: ITopBarNavigationItem) => {
	const { name, to, translation, openInNewTab = false, reloadDocument = false } = item;
	const params = useParams<{ slug: string }>();
	const [isOpen, setIsOpen] = useState(false);

	const isBlokOwnerBlokPage =
		blokPublicStore.state.info.isOwner &&
		item.to === PageRoutes.MyBlokPage &&
		window.location.pathname ===
			PageRoutes.BlokPublicPage.replace(':slug', String(params.slug)).replace(':tab?', '');

	const hasSubcategories = Array.isArray(item.subcategories) && item.subcategories.length > 0;

	if (isMobile) {
		if (hasSubcategories) {
			return (
				<Disclosure>
					{({ open }) => (
						<div className="w-full px-2">
							<Disclosure.Button
								className="self-stretch pl-2 pr-3 py-2 bg-gray-100 rounded-md inline-flex justify-between items-center w-full"
								onClick={() => setIsOpen(!isOpen)}
							>
								<div className="flex items-center gap-3">
									<span className="text-violet-500 text-base font-semibold leading-normal">
										{__(item.translationKey || translation)}
									</span>
								</div>
								<div
									className={`transition-transform duration-200 w-5 h-5 relative overflow-hidden ${
										open ? 'rotate-180' : ''
									}`}
								>
									<Icon name={Icons.ChevronDown} />
								</div>
							</Disclosure.Button>
							<Disclosure.Panel className="mt-3 flex flex-col items-start gap-2 text-shade-black w-full">
								{item.subcategories?.map((sub) => (
									<NavLink
										key={sub.to}
										to={sub.to}
										onClick={() => window.scrollTo(0, 0)}
										className={({ isActive }) =>
											`text-base pl-4 pr-3 py-2 rounded-md inline-flex w-full ${
												isActive ? 'text-primary-500 font-semibold' : 'text-neutral-500 font-normal'
											}`
										}
									>
										{__(sub.translationKey)}
									</NavLink>
								))}
							</Disclosure.Panel>
						</div>
					)}
				</Disclosure>
			);
		}

		return (
			<NavLink
				to={to}
				key={name}
				reloadDocument={reloadDocument}
				target={openInNewTab ? '_blank' : ''}
				onClick={() => window.scrollTo(0, 0)}
				className={({ isActive }) =>
					`block w-full px-5 py-3 text-base ${
						isActive ? 'text-primary-500 font-semibold' : 'text-neutral-500 font-normal'
					}`
				}
			>
				{__(item.translationKey || translation)}
			</NavLink>
		);
	}

	// DESKTOP BEHAVIOR
	if (hasSubcategories) {
		return (
			<div key={item.to} className="relative group w-full text-left">
				<NavLink
					to={item.to}
					onClick={() => window.scrollTo(0, 0)}
					className="cursor-pointer inline-flex h-12 items-center justify-center px-1 pt-1 text-body-semibold-b5 text-primary-500 group-hover:text-primary-700 w-full"
				>
					{__(item.translationKey || translation)}
				</NavLink>
				<div className="absolute left-0 top-full z-10 hidden group-hover:flex flex-col bg-white border border-gray-200 rounded-md shadow-md w-60">
					{item.subcategories?.map((sub) => (
						<NavLink
							key={sub.to}
							to={sub.to}
							onClick={() => window.scrollTo(0, 0)}
							className={({ isActive }) =>
								`px-4 py-2 text-sm hover:text-primary-500 hover:font-semibold ${
									isActive ? 'text-primary-500 font-semibold' : 'text-shade-black font-normal'
								}`
							}
						>
							{__(sub.translationKey)}
						</NavLink>
					))}
				</div>
			</div>
		);
	}

	return (
		<NavLink
			to={to}
			key={name}
			reloadDocument={reloadDocument}
			target={openInNewTab ? '_blank' : ''}
			className={({ isActive }) =>
				`${getClassNames(isActive || isBlokOwnerBlokPage)} ${
					item.element ? 'flex items-center justify-center' : ''
				}`
			}
		>
			{__(translation)}
			{item.element && item.element}
		</NavLink>
	);
};

export default TopNavBarMenuItem;
